import { JournalEntry } from "../../../mock.data";
import {
  FormData,
  JournalLineForm,
  JournalTotals,
} from "./journalEntryValidation";
import { FORM_DEFAULTS, UI_CONFIG } from "./journalEntryConstants";
import moment from "moment";

/**
 * Calculates journal entry totals efficiently
 */
export const calculateJournalTotals = (
  lines: JournalLineForm[]
): JournalTotals => {
  let totalDebit = 0;
  let totalCredit = 0;

  for (const line of lines) {
    totalDebit += line.debit || 0;
    totalCredit += line.credit || 0;
  }

  const difference = totalDebit - totalCredit;

  return {
    totalDebit,
    totalCredit,
    difference,
    isBalanced: Math.abs(difference) < UI_CONFIG.BALANCE_PRECISION,
  };
};

/**
 * Creates a new journal line with default values
 */
export const createNewJournalLine = (id?: string): JournalLineForm => ({
  id: id || `line-${Date.now()}`,
  account_id: "",
  account_code: "",
  account_name: "",
  debit: null,
  credit: null,
  description: "",
});

/**
 * Creates initial form data with default values
 */
export const createInitialFormData = (): FormData => ({
  entry_date: FORM_DEFAULTS.ENTRY_DATE,
  narration: FORM_DEFAULTS.NARRATION,
  reference_id: FORM_DEFAULTS.REFERENCE_ID,
  branch_id: FORM_DEFAULTS.BRANCH_ID,
  currency: FORM_DEFAULTS.CURRENCY,
  journal_type: FORM_DEFAULTS.JOURNAL_TYPE,
  journal_lines: [
    createNewJournalLine("line-1"),
    createNewJournalLine("line-2"),
  ],
});

/**
 * Transforms form data to journal entry format for API
 */
export const transformFormDataToJournalEntry = (
  formData: FormData,
  status: "Draft" | "Posted"
): any => ({
  entry_date: formData.entry_date
    ? moment(formData.entry_date).valueOf()
    : moment().valueOf(),
  narration: formData.narration,
  source_module: "Manual",
  status,
  posted_by: "current-user", // This would come from auth context
  branch_id:
    formData.branch_id === "organization" ? undefined : formData.branch_id,
  currency: formData.currency,
  journal_type: formData.journal_type,
  journal_lines: formData.journal_lines.map((line) => ({
    id: line.id,
    journal_entry_id: "", // Will be set by backend
    account_id: line.account_id,
    account_code: line.account_code,
    account_name: line.account_name,
    debit: line.debit || 0,
    credit: line.credit || 0,
    description: line.description,
  })),
  journal_tags: formData.reference_id
    ? [
        {
          id: `tag-${Date.now()}`,
          journal_entry_id: "", // Will be set by backend
          tag_type: "custom",
          tag_value: formData.reference_id,
          description: "Manual Reference",
        },
      ]
    : undefined,
});

/**
 * Updates a specific journal line in the lines array
 */
export const updateJournalLine = (
  lines: JournalLineForm[],
  lineId: string,
  field: keyof JournalLineForm,
  value: any
): JournalLineForm[] => {
  return lines.map((line) =>
    line.id === lineId ? { ...line, [field]: value } : line
  );
};

/**
 * Updates journal line with account selection
 */
export const updateJournalLineWithAccount = (
  lines: JournalLineForm[],
  lineId: string,
  account: { id: string; accountCode: string; accountName: string }
): JournalLineForm[] => {
  return lines.map((line) =>
    line.id === lineId
      ? {
          ...line,
          account_id: account.id,
          account_code: account.accountCode,
          account_name: account.accountName,
        }
      : line
  );
};

/**
 * Adds a new journal line to the lines array
 */
export const addJournalLine = (lines: JournalLineForm[]): JournalLineForm[] => {
  return [...lines, createNewJournalLine()];
};

/**
 * Removes a journal line from the lines array
 */
export const removeJournalLine = (
  lines: JournalLineForm[],
  lineId: string
): JournalLineForm[] => {
  if (lines.length <= FORM_DEFAULTS.MINIMUM_LINES) {
    return lines; // Minimum lines required
  }
  return lines.filter((line) => line.id !== lineId);
};

/**
 * Handles mutual exclusivity of debit/credit amounts
 */
export const handleAmountChange = (
  lines: JournalLineForm[],
  lineId: string,
  field: "debit" | "credit",
  value: number | null
): JournalLineForm[] => {
  return lines.map((line) => {
    if (line.id === lineId) {
      const updatedLine = { ...line, [field]: value };

      // Clear the opposite field if this field has a value
      if (value !== null && value > 0) {
        const oppositeField = field === "debit" ? "credit" : "debit";
        updatedLine[oppositeField] = null;
      }

      return updatedLine;
    }
    return line;
  });
};
